import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GoogleSignInButton } from "@/features/auth/components/google-signin-button";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	buildE164,
	COUNTRY_CODES,
	COUNTRY_RULES,
	type CountryCode,
	DEFAULT_COUNTRY_CODE,
	validateNationalNumber,
} from "@/features/auth/utils/phone";
import { FloatPanel, StudioButton } from "@/features/studio/theme";

export function PhoneLoginPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const {
		sendOtp,
		isLoading,
		error,
		clearError,
		isAuthenticated,
		needsOnboarding,
		needsProfileSetup,
		getUserType,
	} = useAuthStore();
	const [countryCode, setCountryCode] =
		useState<CountryCode>(DEFAULT_COUNTRY_CODE);
	const [phone, setPhone] = useState("");
	// Field errors appear after the first blur or submit, then track the input.
	const [touched, setTouched] = useState(false);

	const rule = COUNTRY_RULES[countryCode];
	const phoneError = touched
		? validateNationalNumber(countryCode, phone)
		: null;
	let phoneErrorMessage: string | null = null;
	if (phoneError === "empty") {
		phoneErrorMessage = t("auth.phoneErrors.empty");
	} else if (phoneError === "wrongStart") {
		phoneErrorMessage = t(`auth.phoneErrors.wrongStart.${rule.id}`);
	} else if (phoneError) {
		phoneErrorMessage = t(`auth.phoneErrors.${phoneError}`, {
			code: `+${rule.code}`,
			length: rule.length,
		});
	}

	useEffect(() => {
		if (isAuthenticated()) {
			if (needsOnboarding()) {
				navigate("/onboarding", { replace: true });
			} else if (needsProfileSetup()) {
				navigate(`/onboarding/${getUserType()}`, { replace: true });
			} else {
				navigate("/dashboard", { replace: true });
			}
		}
	}, [
		isAuthenticated,
		needsOnboarding,
		needsProfileSetup,
		getUserType,
		navigate,
	]);

	const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const next = COUNTRY_CODES.find((code) => code === e.target.value);
		if (!next) return;
		clearError();
		setCountryCode(next);
		// An empty field has nothing to re-validate; the number is checked
		// against the new country by the derived error above.
		setTouched(touched && phone !== "");
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setTouched(true);
		if (validateNationalNumber(countryCode, phone)) return;

		const e164 = buildE164(countryCode, phone);
		try {
			await sendOtp(e164);
			toast.success(t("auth.otpSentSuccess"));
			const state = location.state as { redirectTo?: string } | null;
			navigate("/verify-otp", {
				state: { phone: e164, redirectTo: state?.redirectTo },
			});
		} catch {
			// The store keeps the backend's message and it is shown inline below.
		}
	};

	return (
		<div className="flex min-h-screen flex-col items-center justify-center px-4 bg-dashboard-warm-350">
			<div className="w-full max-w-md">
				{/* Logo/Header */}
				<div className="mb-8 text-center">
					<h1 className="font-display font-semibold text-[32px] text-dashboard-text mb-2">
						{t("auth.loginTitle")}
					</h1>
					<p className="font-body text-sm text-dashboard-muted">
						{t("auth.loginSubtitle")}
					</p>
				</div>

				{/* Login Card */}
				<FloatPanel elevation={2} padding="40px 32px">
					<h2 className="font-display font-semibold text-xl text-dashboard-text mb-6">
						{t("auth.loginWithPhone")}
					</h2>

					<form onSubmit={handleSubmit} noValidate className="space-y-6">
						{/* Phone Input */}
						<div className="flex flex-col gap-1.5">
							<label
								htmlFor="phone"
								className="font-mono text-xs font-semibold tracking-wider uppercase text-dashboard-muted"
							>
								{t("auth.phoneNumber")}
							</label>
							<div className="flex">
								<select
									aria-label={t("auth.countryCode")}
									value={countryCode}
									onChange={handleCountryChange}
									disabled={isLoading}
									className="h-10 rounded-l-[7px] rounded-r-none border border-r-0 border-dashboard-border bg-dashboard-warm-450/40 pl-3 pr-2 text-sm font-body text-dashboard-muted outline-none focus:border-dashboard-accent"
								>
									{COUNTRY_CODES.map((code) => (
										<option key={code} value={code}>
											{`+${code}`}
										</option>
									))}
								</select>
								<input
									type="tel"
									id="phone"
									value={phone}
									onChange={(e) => {
										clearError();
										// Only allow digits
										setPhone(e.target.value.replace(/\D/g, ""));
									}}
									onBlur={() => setTouched(true)}
									placeholder={rule.placeholder}
									autoComplete="tel-national"
									aria-invalid={phoneErrorMessage ? true : undefined}
									aria-describedby={
										phoneErrorMessage ? "phone-error" : "phone-hint"
									}
									className={`flex-1 h-10 px-4 tracking-widest text-sm font-body text-dashboard-text bg-dashboard-warm-450 border ${
										phoneErrorMessage
											? "border-red-500"
											: "border-dashboard-border"
									} rounded-r-[7px] rounded-l-none outline-none focus:border-dashboard-accent focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-dashboard-accent)_12%,transparent)] transition-all duration-200`}
								/>
							</div>
							{phoneErrorMessage ? (
								<p
									id="phone-error"
									role="alert"
									className="mt-1 text-xs text-dashboard-pill-risk font-body"
								>
									{phoneErrorMessage}
								</p>
							) : (
								<p
									id="phone-hint"
									className="mt-1 text-xs text-dashboard-muted"
								>
									{t("auth.phoneHint")}
								</p>
							)}
						</div>

						{/* Error Display */}
						{error && (
							<div className="rounded-md bg-red-500/10 p-3 text-sm text-dashboard-pill-risk font-body">
								{error}
							</div>
						)}

						{/* Submit Button */}
						<StudioButton
							type="submit"
							variant="primary"
							disabled={isLoading || phone === ""}
							style={{ width: "100%" }}
						>
							{isLoading ? t("auth.sendingOtp") : t("common.continue")}
						</StudioButton>
					</form>

					{/* Sign in with Google (renders only when configured) */}
					<GoogleSignInButton />

					{/* Dev hint */}
					{import.meta.env.VITE_SHOW_DEV_HINTS === "true" && (
						<p className="mt-4 text-center text-xs font-body text-dashboard-muted">
							Dev mode: Use OTP "123456" for any phone number
						</p>
					)}
				</FloatPanel>

				{/* Footer */}
				<p className="mt-6 text-center text-sm font-body text-dashboard-muted">
					{t("auth.termsText")}
				</p>
			</div>
		</div>
	);
}
